import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePharmacy } from "./PharmacyProvider";
import { convertCurrency as convertCurrencyEngine } from "../utils/financeEngine";
import { getFallbackRates } from "../utils/conversionRates";

const CurrencyContext = createContext();
const EMPTY_CURRENCIES = [];

export const CurrencyProvider = ({ children }) => {
	const { pharmacyPOS, refreshConversionRates } = usePharmacy();
	const [currency, setCurrency] = useState(null);

	const ratesRef = useRef(pharmacyPOS?.conversionRates || {});

	useEffect(() => {
		if (pharmacyPOS?.conversionRates) {
			ratesRef.current = pharmacyPOS.conversionRates;
		}
	}, [pharmacyPOS?.conversionRates]);

	useEffect(() => {
		if (pharmacyPOS?.allowedCurrencies?.length > 0) {
			setCurrency(pharmacyPOS.allowedCurrencies[0]);
		}
	}, [pharmacyPOS]);

	const rates = pharmacyPOS?.conversionRates || ratesRef.current;
	const allowedCurrencies = pharmacyPOS?.allowedCurrencies || EMPTY_CURRENCIES;

	const getCurrencySymbol = useCallback((code = null, position = "prefix") => {
		const selectedCode =
			code ||
			currency ||
			allowedCurrencies?.[0] ||
			"INR";

		try {
			const formatter = new Intl.NumberFormat(undefined, {
				style: "currency",
				currency: selectedCode,
				currencyDisplay: "symbol",
			});

			const parts = formatter.formatToParts(0);
			const symbol = parts.find((p) => p.type === "currency")?.value || selectedCode;

			return position === "suffix" ? ` ${symbol}` : symbol;
		} catch {
			return position === "suffix" ? ` ${selectedCode}` : selectedCode;
		}
	}, [allowedCurrencies, currency]);

	const convertCurrency = useCallback((amount, fromCurrency, toCurrency) => {
		if (fromCurrency === toCurrency) {
			if (amount === null || amount === undefined || amount === "") return 0;
			return Number(amount);
		}
		if (amount === null || amount === undefined || amount === "") {
			return 0;
		}

		const activeRates = ratesRef.current;
		if (!activeRates || activeRates[fromCurrency] == null || activeRates[toCurrency] == null) {
			return Number(amount);
		}

		return convertCurrencyEngine(amount, fromCurrency, toCurrency, activeRates).toNumber();
	}, []);

	const refreshRates = useCallback(async () => {
		const result = await refreshConversionRates();
		const fallback = getFallbackRates(pharmacyPOS);
		const effectiveRates = result.rates || fallback.rates;

		if (effectiveRates && Object.keys(effectiveRates).length > 0) {
			ratesRef.current = effectiveRates;
		}

		return {
			rates: effectiveRates,
			baseCurrency: result.baseCurrency || fallback.baseCurrency,
			fromApi: result.success,
		};
	}, [pharmacyPOS, refreshConversionRates]);

	const updateCurrency = useCallback(async (code) => {
		if (!allowedCurrencies?.includes(code)) return;

		await refreshRates();
		setCurrency(code);
	}, [allowedCurrencies, refreshRates]);

	const value = useMemo(
		() => ({
			currency,
			setCurrency,
			updateCurrency,
			getCurrencySymbol,
			allowedCurrencies,
			baseCurrency: pharmacyPOS?.baseCurrency || "INR",
			convertCurrency,
			refreshRates,
			rates,
		}),
		[
			allowedCurrencies,
			convertCurrency,
			currency,
			getCurrencySymbol,
			pharmacyPOS?.baseCurrency,
			rates,
			refreshRates,
			updateCurrency,
		]
	);

	return (
		<CurrencyContext.Provider value={value}>
			{children}
		</CurrencyContext.Provider>
	);
};

export const useCurrency = () => useContext(CurrencyContext);
